import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import {
  getRoadmap,
  saveRoadmap,
  setWeekComplete,
} from "@/lib/roadmap.functions";
import {
  FOCUS_AREAS,
  FEELING_OPTIONS,
  PRIORITY_OPTIONS,
  buildRoadmap,
} from "@/lib/roadmap-data";
import { supabase } from "@/integrations/supabase/client";
import { useMembership } from "@/hooks/useMembership";
import recoveryNotStraightLine from "@/assets/recovery-isnt-straight-line.webp.asset.json";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Sparkles,
  RotateCcw,
  BookOpen,
  Circle,
} from "lucide-react";

export const Route = createFileRoute("/roadmap")({
  head: () => ({
    meta: [
      { title: "Your Recovery Roadmap — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A gentle, personalized 4-week recovery plan based on what you'd like support with.",
      },
      { property: "og:title", content: "Your Recovery Roadmap — Recovery Trauma™" },
      { property: "og:description", content: "A gentle, personalized 4-week recovery plan based on what you'd like support with." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/roadmap" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/roadmap" }],
  }),
  component: RoadmapPage,
});

function RoadmapPage() {
  const membership = useMembership();

  if (membership.loading) {
    return (
      <div
        className="flex min-h-[60svh] items-center justify-center text-sm text-muted-foreground"
        role="status"
      >
        Loading your roadmap…
      </div>
    );
  }

  if (!membership.isAuthed) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center">
        <Sparkles className="mx-auto h-9 w-9 text-[var(--gold-deep)]" />
        <h1 className="mt-4 font-display text-3xl text-foreground">
          Your Recovery Roadmap
        </h1>
        <p className="mt-3 text-muted-foreground">
          Sign in to create your gentle 4-week plan and keep it saved privately
          to your account. You can change or restart it whenever you like.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/login"
            className="rounded-full bg-[var(--gold-deep)] px-6 py-3 text-sm font-medium text-white"
          >
            Sign in
          </Link>
          <Link
            to="/membership"
            className="rounded-full border border-gold/40 px-6 py-3 text-sm font-medium text-foreground"
          >
            See membership
          </Link>
        </div>
      </div>
    );
  }

  return <RoadmapAuthed />;
}

function RoadmapAuthed() {
  const fetchRoadmap = useServerFn(getRoadmap);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["roadmap"],
    queryFn: fetchRoadmap,
    retry: 1,
  });

  const [editing, setEditing] = useState(false);

  if (isLoading) {
    return (
      <div
        className="flex min-h-[60svh] items-center justify-center text-sm text-muted-foreground"
        role="status"
      >
        Loading your roadmap…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center">
        <h1 className="font-display text-2xl text-foreground">
          We couldn't load your roadmap
        </h1>
        <p className="mt-3 text-muted-foreground">
          This is usually a temporary connection problem. Please refresh the
          page, and if it keeps happening you can contact support.
        </p>
        <Link
          to="/support"
          className="mt-6 inline-block rounded-full border border-gold/40 px-6 py-3 text-sm text-foreground"
        >
          Contact support
        </Link>
      </div>
    );
  }

  const hasRoadmap = (data?.focusAreas?.length ?? 0) > 0 && !editing;

  if (!hasRoadmap) {
    return (
      <RoadmapWizard
        existing={data}
        onCancel={() => setEditing(false)}
        onDone={() => setEditing(false)}
      />
    );
  }

  return <RoadmapView data={data!} onEdit={() => setEditing(true)} />;
}

/* ============================================================
   WIZARD — 5 steps: welcome → topics → feeling → priority → done
   ============================================================ */

type WizardData = {
  focusAreas: string[];
  feeling: string | null;
  priority: string | null;
} | undefined;

function RoadmapWizard({
  existing,
  onCancel,
  onDone,
}: {
  existing: WizardData;
  onCancel: () => void;
  onDone: () => void;
}) {
  const saveFn = useServerFn(saveRoadmap);
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [topics, setTopics] = useState<string[]>(existing?.focusAreas ?? []);
  const [feeling, setFeeling] = useState<string | null>(
    existing?.feeling ?? null,
  );
  const [priority, setPriority] = useState<string | null>(
    existing?.priority ?? null,
  );

  const save = useMutation({
    mutationFn: () =>
      saveFn({ data: { focusAreas: topics, feeling, priority } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roadmap"] });
      setStep(4);
      onDone();
    },
  });

  const StepIndicator = () => (
    <div className="mb-8 flex items-center justify-center gap-2">
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className={`h-1.5 w-8 rounded-full transition ${
            i <= step ? "bg-[var(--gold-deep)]" : "bg-border"
          }`}
        />
      ))}
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 md:py-16">
      <StepIndicator />

      {/* ───── Step 1: Welcome ───── */}
      {step === 0 && (
        <div className="text-center">
          <Sparkles className="mx-auto h-9 w-9 text-[var(--gold-deep)]" />
          <h1 className="mt-4 font-display text-3xl text-foreground md:text-4xl">
            Build Your Recovery Roadmap
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Tell us what you'd like support with and we'll create a gentle
            recovery plan tailored to you.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            You can update this at any time.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="btn-gold inline-flex items-center gap-2"
            >
              Let's Begin <ArrowRight className="h-4 w-4" />
            </button>
            {(existing?.focusAreas?.length ?? 0) > 0 && (
              <button
                type="button"
                onClick={onCancel}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* ───── Step 2: Topics ───── */}
      {step === 1 && (
        <div>
          <Heading
            eyebrow="Step 1 of 3"
            title="What would you like support with?"
            sub="Choose anything that feels relevant — there's no wrong answer. You can change this later."
          />
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {FOCUS_AREAS.map((f) => {
              const active = topics.includes(f.slug);
              return (
                <button
                  key={f.slug}
                  type="button"
                  onClick={() =>
                    setTopics((prev) =>
                      prev.includes(f.slug)
                        ? prev.filter((s) => s !== f.slug)
                        : [...prev, f.slug],
                    )
                  }
                  className={`flex items-start gap-3 rounded-2xl border p-4 text-start transition ${
                    active
                      ? "border-gold bg-gold/10 shadow-[var(--shadow-gold)]"
                      : "border-border bg-card hover:border-gold/40"
                  }`}
                >
                  <span className="text-xl" aria-hidden>
                    {f.emoji}
                  </span>
                  <span className="flex-1">
                    <span className="block font-display text-base text-foreground">
                      {f.label}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {f.blurb}
                    </span>
                  </span>
                  {active && (
                    <CheckCircle2 className="h-5 w-5 text-[var(--gold-deep)]" />
                  )}
                </button>
              );
            })}
          </div>
          <NavButtons
            onBack={() => setStep(0)}
            onNext={() => setStep(2)}
            disabled={topics.length === 0}
          />
        </div>
      )}

      {/* ───── Step 3: Feeling ───── */}
      {step === 2 && (
        <div>
          <Heading
            eyebrow="Step 2 of 3"
            title="How are you feeling recently?"
            sub="There's no wrong answer. This helps us pace things gently."
          />
          <div className="mt-8 grid gap-3 sm:grid-cols-5">
            {FEELING_OPTIONS.map((o) => {
              const active = feeling === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setFeeling(o.value)}
                  className={`flex flex-col items-center gap-2 rounded-2xl border p-4 transition ${
                    active
                      ? "border-gold bg-gold/10 shadow-[var(--shadow-gold)]"
                      : "border-border bg-card hover:border-gold/40"
                  }`}
                >
                  <span className="text-2xl" aria-hidden>
                    {o.emoji}
                  </span>
                  <span className="text-center text-sm font-medium text-foreground">
                    {o.label}
                  </span>
                </button>
              );
            })}
          </div>
          <NavButtons
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            disabled={!feeling}
          />
        </div>
      )}

      {/* ───── Step 4: Priority ───── */}
      {step === 3 && (
        <div>
          <Heading
            eyebrow="Step 3 of 3"
            title="What feels most important right now?"
            sub="We'll weave this into your weekly plan."
          />
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {PRIORITY_OPTIONS.map((o) => {
              const active = priority === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setPriority(o.value)}
                  className={`flex items-center gap-3 rounded-2xl border p-4 text-start transition ${
                    active
                      ? "border-gold bg-gold/10 shadow-[var(--shadow-gold)]"
                      : "border-border bg-card hover:border-gold/40"
                  }`}
                >
                  <span className="text-2xl" aria-hidden>
                    {o.emoji}
                  </span>
                  <span className="flex-1 font-display text-base text-foreground">
                    {o.label}
                  </span>
                  {active && (
                    <CheckCircle2 className="h-5 w-5 text-[var(--gold-deep)]" />
                  )}
                </button>
              );
            })}
          </div>
          <div className="mt-10 flex flex-col items-center gap-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-foreground hover:border-gold/40"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button
                type="button"
                disabled={!priority || save.isPending}
                onClick={() => save.mutate()}
                className="btn-gold inline-flex items-center gap-2 disabled:opacity-50"
              >
                {save.isPending
                  ? "Building your roadmap…"
                  : "Generate My Roadmap"}{" "}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            {save.isError && (
              <p className="text-sm text-destructive">
                Something went wrong. Please try again.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Heading({
  eyebrow,
  title,
  sub,
}: {
  eyebrow: string;
  title: string;
  sub: string;
}) {
  return (
    <div className="text-center">
      <p className="text-xs font-medium uppercase tracking-wider text-[var(--gold-deep)]">
        {eyebrow}
      </p>
      <h1 className="mt-2 font-display text-3xl text-foreground md:text-4xl">
        {title}
      </h1>
      <p className="mx-auto mt-3 max-w-xl text-muted-foreground">{sub}</p>
    </div>
  );
}

function NavButtons({
  onBack,
  onNext,
  disabled,
}: {
  onBack: () => void;
  onNext: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="mt-10 flex items-center justify-center gap-3">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-foreground hover:border-gold/40"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={disabled}
        className="btn-gold inline-flex items-center gap-2 disabled:opacity-50"
      >
        Continue <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

/* ============================================================
   ROADMAP VIEW — 4 weeks + library + completion
   ============================================================ */

function RoadmapView({
  data,
  onEdit,
}: {
  data: {
    focusAreas: string[];
    feeling: string | null;
    priority: string | null;
    completedWeeks: number[];
  };
  onEdit: () => void;
}) {
  const setComplete = useServerFn(setWeekComplete);
  const qc = useQueryClient();
  const toggleWeek = useMutation({
    mutationFn: (vars: { week: number; complete: boolean }) =>
      setComplete({ data: vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["roadmap"] }),
  });

  const weeks = buildRoadmap(data.focusAreas, data.priority);
  const completed = new Set(data.completedWeeks);
  const totalDone = data.completedWeeks.length;

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      {/* ───── Header ───── */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-[var(--gold-deep)]">
            Your Recovery Path
          </p>
          <h1 className="mt-1 font-display text-3xl text-foreground md:text-4xl">
            A gentle 4-week plan, just for you
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Focus:{" "}
            {data.focusAreas
              .map((s) => FOCUS_AREAS.find((f) => f.slug === s)?.label ?? s)
              .join(" · ")}
          </p>
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-2 self-start rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-foreground hover:border-gold/40"
        >
          <RotateCcw className="h-4 w-4" /> Update plan
        </button>
      </div>

      {/* ───── Progress ───── */}
      <div className="mb-8 rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-foreground">
            {totalDone === 0
              ? "Begin whenever feels right — no rush."
              : totalDone === 4
              ? "You've moved through every week. That matters."
              : `${totalDone} of 4 weeks marked complete`}
          </p>
          <span className="text-xs text-muted-foreground">
            Completion is optional
          </span>
        </div>
        <div className="mt-3 flex gap-2">
          {[1, 2, 3, 4].map((w) => (
            <span
              key={w}
              className={`h-2 flex-1 rounded-full transition ${
                completed.has(w) ? "bg-[var(--gold-deep)]" : "bg-border"
              }`}
            />
          ))}
        </div>
      </div>

      {/* ───── Gentle reminder illustration ───── */}
      <figure className="mb-8 overflow-hidden rounded-2xl border border-gold/30 bg-card shadow-[var(--shadow-gold)]">
        <img
          src={recoveryNotStraightLine.url}
          alt="Illustrated reminder: Recovery isn't a straight line — it's okay to take your time. Every step still counts."
          className="w-full"
          loading="lazy"
        />
        <figcaption className="px-4 py-3 text-center text-sm italic text-foreground/70">
          Recovery isn't a straight line — every step still counts.
        </figcaption>
      </figure>

      {/* ───── Weeks ───── */}
      <div className="space-y-6">
        {weeks.map((w) => {
          const done = completed.has(w.week);
          return (
            <section
              key={w.week}
              className="rounded-3xl border border-border bg-card p-6 shadow-sm"
            >
              <div className="mb-4 flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span
                    className={`flex h-10 w-10 items-center justify-center rounded-full font-display text-lg ${
                      done
                        ? "bg-[var(--gold-deep)] text-white"
                        : "bg-gold/15 text-[var(--gold-deep)]"
                    }`}
                  >
                    {w.week}
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">
                      Week {w.week}
                    </p>
                    <h2 className="font-display text-xl text-foreground">
                      {w.theme}
                    </h2>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    toggleWeek.mutate({ week: w.week, complete: !done })
                  }
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                    done
                      ? "border-[var(--gold-deep)] bg-gold/10 text-[var(--gold-deep)]"
                      : "border-border bg-background text-muted-foreground hover:border-gold/40 hover:text-foreground"
                  }`}
                >
                  {done ? (
                    <>
                      <CheckCircle2 className="h-4 w-4" /> Complete
                    </>
                  ) : (
                    <>
                      <Circle className="h-4 w-4" /> Mark complete
                    </>
                  )}
                </button>
              </div>

              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Focus
              </p>
              <ul className="grid gap-3 sm:grid-cols-2">
                {w.steps.map((s) => (
                  <li
                    key={s.title}
                    className="rounded-2xl border border-border/60 bg-background/60 p-4"
                  >
                    <p className="font-display text-base text-foreground">
                      {s.title}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {s.body}
                    </p>
                    {s.to && (
                      <Link
                        to={s.to}
                        className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[var(--gold-deep)] hover:underline"
                      >
                        Open <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    )}
                  </li>
                ))}
              </ul>

              <div className="mt-5 flex items-start gap-3 rounded-2xl border border-border/60 bg-gold/5 p-4">
                <BookOpen className="mt-0.5 h-5 w-5 text-[var(--gold-deep)]" />
                <div className="flex-1">
                  <p className="text-xs font-medium uppercase tracking-wider text-[var(--gold-deep)]">
                    Recommended library
                  </p>
                  <Link
                    to={w.library.to}
                    className="mt-0.5 inline-flex items-center gap-1 font-display text-base text-foreground hover:underline"
                  >
                    {w.library.title}{" "}
                    <ArrowRight className="h-3.5 w-3.5 text-[var(--gold-deep)]" />
                  </Link>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <p className="mt-8 text-center text-xs text-muted-foreground">
        Educational and self-guided. Not a substitute for therapy or clinical
        care. You can update your roadmap anytime.
      </p>
    </div>
  );
}
